import { Form, Formik } from "formik";
import moment from "moment";
import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SideNavLayout from "src/components/layouts/SideNavLayout/SideNavLayout";
import { AssignBatchesValues } from "src/models/Student.model";
import { useAddInstallmentMutation } from "src/services/InstallmentServices";
import { showToast } from "src/utils/showToaster";
import { array, object, string } from "yup";
import ProceedDialogBox from "../../ProceedDialogBox";
import ApplyCouponWrapper from "../Coupon/ApplyCouponWrapper";
import AssignBatches from "./AssignBatches";

const AssignBatchesWrapper = () => {
  const [isShowCouponDialog, setIsShowCouponDialog] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const [couponData, setCouponData] = useState<any>();
  const { studentId = "" } = useParams();
  const navigate = useNavigate();
  const [addInstallment , addInstallmentInfo] = useAddInstallmentMutation();

  const formikRef = useRef<any>(null);

  // Form Initial Values
  const initialValues: AssignBatchesValues = {
    batch: "",
    totalFee: "",
    couponCode: "",
    couponDiscount: "",
    discountType: "",
    payableAmount: "",
    receivedAmount: "",
    dueAmount: "",
    installment: [
      {
        date: "",
        amount: "",
      },
    ],
    mode: "",
    accountId: "",
    paymentRemark: "",
  };

  // Validation Schema
  const validationSchema = object().shape({
    batch: object().required("Please select batch"),
    receivedAmount: string()
      .required("Please enter received amount")
      .test(
        "receivedAmtTest",
        "Received amount must be less than payable amount",
        (value, context) => {
          const { payableAmount } = context.parent;
          return Number(value || 0) <= Number(payableAmount || 0);
        }
      ),
    installment: array()
      .of(
        object({
          date: string().test(
            "dateTest",
            "Please select date",
            (value: any, context: any) => {
              const { context: parentData } = context.options;
              const { dueAmount } = parentData;
              return Number(dueAmount || 0) === 0 || value;
            }
          ),
          amount: string().test(
            "amountTest",
            "Please enter amount",
            (value: any, context: any) => {
              const { context: parentData } = context.options;
              const { dueAmount } = parentData;
              return Number(dueAmount || 0) === 0 || value;
            }
          ),
        })
      )
      .test(
        "installmentTest",
        "Installment amount and due amount must me equal",
        (value, context) => {
          const { dueAmount } = context.parent;
          const installmentAmount = value?.reduce((acc, el) => {
            acc += Number(el?.amount || 0);
            return acc;
          }, 0);
          return Number(installmentAmount || 0) === Number(dueAmount || 0);
        }
      ),
    accountId: object().test(
      "receivedAmtTest",
      "Please select account",
      (value, context) => {
        const { receivedAmount } = context.parent;
        if (receivedAmount === "0") {
          return true;
        } else {
          if (value) {
            return true;
          }
          return false;
        }
      }
    ),
    mode: object().test(
      "receivedAmtTest",
      "Please select payment mode",
      (value, context) => {
        const { receivedAmount } = context.parent;
        if (receivedAmount === "0") {
          return true;
        } else {
          if (value) {
            return true;
          }
          return false;
        }
      }
    ),
  });

  // Handle Submit
  const handleSubmit = () => {
    setShowDialog(true);
  };

  const handleConfirm = () => {
    const installmentDate =
      Number(formikRef?.current?.values?.dueAmount || 0) > 0
        ? formikRef?.current?.values?.installment?.map((data: any) => {
            return {
              dueDate: moment(data?.date).format("YYYY-MM-DD"),
              amount: Number(data?.amount || 0),
            };
          })
        : [];

    const formattedValues = {
      totalFees: Number(formikRef?.current?.values?.totalFee || 0),
      coupon: formikRef?.current?.values?.couponCode || "",
      receivedAmount: Number(formikRef?.current?.values?.receivedAmount || 0),
      studentId: studentId,
      batchId: formikRef?.current?.values?.batch?._id,
      remark: formikRef?.current?.values?.paymentRemark,
      accountId: formikRef?.current?.values?.accountId?.value,
      mode: formikRef?.current?.values?.mode?.value || "CASH",
      installmentData:
        installmentDate?.sort(
          (a: any, b: any) =>
            new Date(a?.dueDate)?.getTime() - new Date(b?.dueDate)?.getTime()
        ) || [],
    };
    addInstallment(formattedValues).then((res: any) => {
      if (res?.error) {
        showToast("error", res?.error?.data?.message);
      } else {
        if (res?.data?.status) {
          showToast("success", res?.data?.message);
          formikRef?.current?.resetForm();
          navigate(`/student/edit/${studentId}/batches`);
        } else {
          showToast("error", res?.data?.message);
        }
      }
      formikRef?.current?.setSubmitting(false);
    });
  };

  return (
    <SideNavLayout>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        innerRef={formikRef}
      >
        {(formikProps) => (
          <Form>
            <AssignBatches
              formikProps={formikProps}
              onCouponClick={() => setIsShowCouponDialog(true)}
              couponData={couponData}
            />
          </Form>
        )}
      </Formik>
      {isShowCouponDialog && (
        <ApplyCouponWrapper
          onClose={() => setIsShowCouponDialog(false)}
          setCouponData={setCouponData}
          studentId={studentId}
        />
      )}

      {showDialog && (
        <ProceedDialogBox 
        isLoading ={addInstallmentInfo?.isLoading}
          title={`${formikRef?.current?.values?.batch?.batchName}`}
          text={{
            batchName: formikRef?.current?.values?.batch?.batchName,
            Timing: formikRef?.current?.values?.batch?.time,
            courseName: formikRef?.current?.values?.batch?.courseName,
            teachers: formikRef?.current?.values?.batch?.trainers,
          }}
          onConfirm={handleConfirm}
          onCancel={() => setShowDialog(false)}
        />
      )}
    </SideNavLayout>
  );
};

export default AssignBatchesWrapper;
